import './assets/main.css';
import { createApp } from 'vue';
import App from './App.vue';
import router from './router';
import Toast, { type PluginOptions as ToastOptions } from 'vue-toastification';
import { autoAnimatePlugin } from '@formkit/auto-animate/vue';
import { createVfm } from 'vue-final-modal';
import { Icon } from '@iconify/vue';
import floatingVue from 'floating-vue';
// import css for tooltips
import 'floating-vue/dist/style.css';

// Import the CSS or use your own!
import 'vue-toastification/dist/index.css';
import 'vue-final-modal/style.css';
import { initializePublicConfig, publicConfig, validatePublicConfig } from '@/state/config.ts';
import { initializeFeatureFlags, isFeatureEnabled, onFeatureFlagsChanged } from '@/composables/use-feature-flags.ts';
import { installClientDiagnostics } from '@/services/client-diagnostics.ts';

await initializePublicConfig();
validatePublicConfig();
await initializeFeatureFlags().ready;

const app = createApp(App);

if (publicConfig.sentryDsn && window.location.hostname !== 'localhost') {
  try {
    const Sentry = await import('@sentry/vue');
    Sentry.init({
      app,
      dsn: publicConfig.sentryDsn,
      // Setting this option to true will send default PII data to Sentry.
      // For example, automatic IP address collection on events
      sendDefaultPii: true,
      integrations: [Sentry.browserTracingIntegration({ router })],
      // Tracing
      tracesSampleRate: 1.0, // Capture 100% of the transactions
      // Logs
      enableLogs: true,
    });
  } catch {
    // Local diagnostics still install below; observability startup must not block the SPA.
  }
}

installClientDiagnostics(app, router);
app.use(router);
onFeatureFlagsChanged(() => {
  const key = router.currentRoute.value.meta.featureFlag;
  if (key && !isFeatureEnabled(key)) {
    void router.replace({ path: '/feature-unavailable', query: { feature: key, from: router.currentRoute.value.fullPath } });
  }
});

const options: ToastOptions = {
  // You can set your default options here
};

app.use(Toast, options);

app.use(autoAnimatePlugin);

app.use(createVfm());

app.use(floatingVue);

app.directive('focus', {
  mounted: (el) => el.focus(),
});

app.component('icon', Icon);

app.mount('#app');
